<template>
  <div v-if="form && form.length" :key="cpmKey" class="pr-5">
    <dropdown-button
      :title="$t('common.bulk_actions')"
      class="bulk-selection inline-block"
      :actions="bulkSelection"
      :btn-classes="'md-success'"
    ></dropdown-button>
    <md-table class="mb-10">
      <md-table-row>
        <md-table-head>
          {{ $t("zoom_meeting.invitee_info") }}
        </md-table-head>
        <md-table-head>
          {{ $t("zoom_meeting.email") }}
        </md-table-head>
        <md-table-head>
          {{ $t("zoom_meeting.class_name") }}
        </md-table-head>
        <md-table-head>
          {{ $t("zoom_meeting.join_time") }}
        </md-table-head>
        <md-table-head>
          {{ $t("zoom_meeting.leave_time") }}
        </md-table-head>
        <md-table-head class="w-[150px]">
          {{ $t("zoom_meeting.status") }}
        </md-table-head>
        <md-table-head class="w-[350px]">
          {{ $t("zoom_meeting.comment") }}
        </md-table-head>
      </md-table-row>
      <LogItem
        v-for="(item, index) in form"
        :itemProps="item"
        :itemDefaultProps="defaultData[index]"
        :key="'attendance' + index"
        :canUpdate="!canUpdateAttendanceLog"
      >
      </LogItem>
    </md-table>
    <p class="font-medium py-2 text-lg">
      {{ $t("zoom_meeting.comment_for_whole_class") }}
    </p>
    <div class="md-layout-item md-size-100 p-0 border border-gray-300">
      <md-field>
        <md-textarea
          :disabled="!canUpdateAttendanceLog"
          :placeholder="$t('smi_sri.comment')"
          v-model="comment"
        ></md-textarea>
      </md-field>
    </div>
    <div class="flex justify-center mt-5">
      <md-button
        v-if="canUpdateAttendanceLog"
        type="submit"
        @click="updateAttendanceLog"
        class="md-success ml-2"
        >{{ $t("common.save") }}
      </md-button>
    </div>
  </div>
  <no-data-found class="py-5" v-else></no-data-found>
</template>
<script>
import cloneDeep from "lodash/cloneDeep";
import LogItem from "./AttendanceLogItem";
import { service_names } from "@/constants/Services";
import {
  convertTimeByTimezone,
  convertDateSendServer
} from "@/helpers/datetime-helper";
import moment from "moment";
import { Message } from "@/helpers/utils";
import { hasValidRole } from "@/helpers/role-helper";
import Role from "@/constants/Role";
import { ZoomMeeting } from "@/constants/Permissions";
import { mapGetters } from "vuex";
import DropdownButton from "@/components/Base/DropdownButton";

export default {
  components: { LogItem, DropdownButton },
  inject: ["fetchReportByDateTime"],
  props: [
    "data",
    "defaultData",
    "dateSelected",
    "currentTimezone",
    "fromTimeSelected",
    "staticInfo",
    "isScheduleMeeting",
    "startDate",
    "dateSelectedByQuery"
  ],
  data() {
    const self = this;
    return {
      params: {},
      cpmKey: null,
      comment: "",
      form: null,
      serverFormat: "YYYY-MM-DD HH:mm:ss",
      isoFormat: "YYYY-MM-DD[T]HH:mm:ss",
      bulkSelection: [
        {
          content: "zoom_meeting.present_all",
          action: () => {
            self.selectionForAll("Present");
          }
        },
        {
          content: "zoom_meeting.excused_absence_all",
          action: () => {
            self.selectionForAll("Ex.absence");
          }
        },
        {
          content: "zoom_meeting.excused_tardy_all",
          action: () => {
            self.selectionForAll("Ex.tardy");
          }
        },
        {
          content: "zoom_meeting.unexcused_absence_all",
          action: () => {
            self.selectionForAll("Unex.absence");
          }
        },
        {
          content: "zoom_meeting.unexcused_tardy_all",
          action: () => {
            self.selectionForAll("Unex.tardy");
          }
        }
      ]
    };
  },
  computed: {
    ...mapGetters("user", {
      userRole: "role"
    }),
    canUpdateAttendanceLog() {
      return (
        this.$can(ZoomMeeting.actions.updateReport, ZoomMeeting.subject) ||
        hasValidRole(this.userRole, Role.Teacher)
      );
    }
  },
  watch: {
    data: function(newVal) {
      this.form = newVal;
      this.cpmKey++;
    },
    staticInfo: function(newVal) {
      this.comment = newVal.data.zoom_meeting_comment;
    }
  },
  methods: {
    selectionForAll(type) {
      this.form.forEach(item => {
        item.status = type;
      });
    },
    checkAnyChange(currentItem, defaultItem) {
      return JSON.stringify(currentItem) !== JSON.stringify(defaultItem);
    },
    convertToUTCTime(date, time, currentTimezone) {
      date = moment(date, "YYYY-MM-DD HH:mm:ss").format("YYYY-MM-DD");
      if (!time) return null;
      let dateTime = date + " " + time;
      return convertTimeByTimezone(
        dateTime,
        currentTimezone,
        "Etc/UTC",
        this.serverFormat
      );
    },
    updateAttendanceLog() {
      const listAttendanceLogChanged = this.form.filter((item, index) => {
        return this.checkAnyChange(item, this.defaultData[index]) && item;
      });
      const setDate = dateTime => {
        if (this.dateSelectedByQuery) return this.dateSelectedByQuery;
        if (this.isScheduleMeeting) {
          return convertTimeByTimezone(
            dateTime,
            this.currentTimezone,
            "Etc/UTC",
            "YYYY-MM-DD HH:mm"
          );
        }
        return moment(this.startDate, this.isoFormat).format(
          "YYYY-MM-DD HH:mm"
        );
      };
      let dateTime =
        moment(this.dateSelected).format("YYYY-MM-DD") +
        " " +
        this.fromTimeSelected;
      const formRequest = {
        date: setDate(dateTime),
        zoom_meeting_comment: this.comment,
        data_attendance_logs: listAttendanceLogChanged.map(item => {
          return {
            join_time: this.convertToUTCTime(
              item.date,
              item.from_join_time,
              this.currentTimezone
            ),
            leave_time: this.convertToUTCTime(
              item.date,
              item.from_leave_time,
              this.currentTimezone
            ),
            id: item.id,
            description: item.description || null,
            status: item.status || null,
            calendar_id: item.calendar_id
          };
        })
      };
      const zoomId = this.$route.params.id;
      this.$axios
        .put(
          `${service_names.school}/attendance-logs/zoom-meeting/${zoomId}`,
          formRequest
        )
        .then(() => {
          Message.success(this.$t("common.save_success"));
          this.fetchReportByDateTime();
        });
    }
  }
};
</script>
<style lang="scss" scoped></style>
